import type {
  ArticleDataObject,
  DisplaySetting,
  Paywall,
  SanitizedFeedSectionArticle,
} from "@app/types/Cue";
import { ArticleAuthorType } from "@pages/Article/utils/enums";
import { FeedAuthor, FeedMedia } from "@pages/Article/utils/types";
import { FeedTag } from "@pages/Keyword/utils/types";
import { TagType } from "@pages/Section/types";
import { Name } from "@typings/enums";
import { getArticleBlurb } from "@util/helpers";

export const sanitizeFeedSectionArticleObjects = (
  articles: ArticleDataObject[]
): SanitizedFeedSectionArticle[] => {
  return (
    articles?.map((article) => {
      const kickerFromElements = article.elements?.find(
        (element) =>
          element.type === "kicker" &&
          Array.isArray(element.fields) &&
          element.fields?.some((field) => field.name === "kicker")
      );

      const tags: FeedTag[] =
        article.tags?.map((tag) => {
          return {
            id: tag.id || "",
            name: tag.name || "",
            type: tag.type || TagType.Keyword,
            urlPath: tag.urlPath || "",
          };
        }) || [];

      const _authors: FeedAuthor[] =
        article.authors?.flatMap((author) => {
          const profiles = author.profiles || [];

          const authorProfiles = profiles
            .filter((p) => p.content?.type === "authorProfile")
            .sort(
              (a, b) =>
                new Date(b.content?.edited || 0).getTime() -
                new Date(a.content?.edited || 0).getTime()
            );

          if (authorProfiles.length === 0) return [];

          const latestProfile = authorProfiles[0].content;

          return [
            {
              id: Number(author.id),
              name: latestProfile.fields?.name || author.name?.trim() || "",
              image:
                latestProfile.headshotImage?.[0]?.content.fields[
                  "square_320-caas"
                ]?.url || "",
              urlPath: latestProfile.urlPath || "",
              type: ArticleAuthorType.AUTHOR_PROFILE,
            },
          ];
        }) || [];

      const _media: FeedMedia[] =
        article.media?.map((item) => {
          return {
            id: item.content?.id || "",
            type: item.content?.type,
            landscape: {
              url: item.content?.fields?.["landscape-caas"]?.url || "",
              width: item.content?.fields?.["landscape-caas"]?.width || 0,
              height: item.content?.fields?.["landscape-caas"]?.height || 0,
            },
            portrait: {
              url: item.content?.fields?.["portrait-caas"]?.url || "",
              width: item.content?.fields?.["portrait-caas"]?.width || 0,
              height: item.content?.fields?.["portrait-caas"]?.height || 0,
            },
            original: {
              url: item.content?.fields?.["original-caas"]?.url || "",
              width: item.content?.fields?.["original-caas"]?.width || 0,
              height: item.content?.fields?.["original-caas"]?.height || 0,
            },
            square: {
              url: item.content?.fields?.["square-caas"]?.url || "",
              width: item.content?.fields?.["square-caas"]?.width || 0,
              height: item.content?.fields?.["square-caas"]?.height || 0,
            },
            caption: item.summary?.find(({ key }) => key === Name.Caption)
              ?.value,
            alt: item.summary?.find(({ key }) => key === Name.Alt)?.value || "",
          };
        }) || [];

      const SanitizedFeedSectionArticle: SanitizedFeedSectionArticle = {
        id: article.id,
        title: article.displaySetting?.displayHeadline || article.title,
        urlPath: article.displaySetting?.externalURL || article.urlPath,
        created: article.created || "",
        updated: article.updated || "",
        edited: article.edited || "",
        paywall:
          article.paywall ||
          <Paywall>{
            printFlag: false,
            contentAccess: "0",
            copyright: "",
          },
        media: _media,
        slug: article.displaySetting?.externalURL || article.slug || "",
        displaySetting:
          article.displaySetting ||
          <DisplaySetting>{
            displayHeadline: "",
            externalURL: "",
            hasUpdatedTimeDisplayed: false,
          },
        kicker:
          article.kicker ||
          (kickerFromElements && {
            id: kickerFromElements.id,
            type: kickerFromElements.type,
            fields: kickerFromElements.fields,
          }) ||
          undefined,
        blurb: getArticleBlurb(article) || "",
        sections: article.sections || [],
        authors: _authors || [],
        relatedContributorProfile: article.relatedContributorProfile || [],
        tags: tags || [],
        elements: article.elements,
      };

      return SanitizedFeedSectionArticle;
    }) || []
  );
};
